/**
 * renderCaption - Bridge for caption rendering in server-side rendering
 *
 * This module provides a window API that bridges Puppeteer to the Caption
 * component's existing rendering logic. Works by setting window.easil.playbackTime,
 * which the Caption component polls via requestAnimationFrame - the same
 * mechanism used by timeline scrubbing in the editor.
 *
 * Usage by Puppeteer:
 * ```javascript
 * await window.renderCaption(elementId, 0);    // update caption at time 0
 * await window.renderCaption(elementId, 1500); // update caption at time 1500ms
 * ```
 *
 * Prerequisites:
 * - Caption component must be mounted
 */

/**
 * Animation configuration for caption segment animations.
 * Duration values match those defined in Caption.module.css.
 * Initial styles match the "from" keyframe values - used when seekTime is 0
 * to show the animation start frame (same pattern as animationUtils.js).
 */
const CAPTION_ANIMATION_CONFIG: Record<
  string,
  {
    duration: number;
    className: string;
    initialStyles?: Record<string, string>;
  }
> = {
  fadeIn: {
    duration: 300,
    className: "animateFadeIn",
    initialStyles: { opacity: "0" }
  },
  rise: {
    duration: 400,
    className: "animateRise",
    initialStyles: { opacity: "0", transform: "translateY(20px)" }
  },
  zoom: {
    duration: 400,
    className: "animateZoom",
    initialStyles: { opacity: "0", transform: "scale(0.7)" }
  },
  appear: { duration: 0, className: "animateAppear" } // No animation
};

/**
 * Parent media element data structure (video/audio)
 */
interface ParentMediaData {
  type: string;
  delay?: number;
  trimStart?: number;
  startOffset?: number;
}

/**
 * Caption element data structure from renderDesignData
 */
interface CaptionElementData {
  type: string;
  videoElementId?: string;
  delay?: number;
  segments?: Array<{
    start?: number;
    startTime?: number;
    end?: number;
    endTime?: number;
  }>;
  displaySettings?: {
    captionAnimation?: string;
  };
}

/**
 * Calculates the effective delay for a caption element.
 * This is the time offset from the start of the timeline to when the caption should start.
 *
 * @param captionElementId - The ID of the caption element
 * @returns The effective delay in milliseconds, or 0 if not found
 */
const getEffectiveDelay = (captionElementId: string): number => {
  // Get design data from window (set by RenderContainer)
  const designData = window.renderDesignData;
  if (!designData) return 0;

  // Get caption element data
  const captionData = designData.elements[captionElementId] as
    | CaptionElementData
    | undefined;
  if (!captionData || captionData.type !== "caption") return 0;

  // Get parent media element (video/audio) to determine delay
  // This mirrors the logic in Caption.js componentDidUpdate
  const videoElementId = captionData.videoElementId;
  const parentMedia = videoElementId
    ? (designData.elements[videoElementId] as ParentMediaData | undefined)
    : undefined;

  // Calculate effective delay (when caption starts on timeline)
  // Priority:
  // 1. Parent media delay (if parent media is on same layer)
  // 2. captionDelays lookup (for cross-layer rendering where parent media isn't available)
  // 3. Caption's own delay
  // 4. Default to 0
  if (parentMedia?.delay !== undefined) {
    // Parent media available - calculate delay including audio trimStart
    const audioTrimStart =
      parentMedia.type === "audio" ? (parentMedia.trimStart ?? 0) : 0;
    return parentMedia.delay + audioTrimStart;
  } else if (
    videoElementId &&
    designData.captionDelays?.[videoElementId] !== undefined
  ) {
    // Use pre-computed delay from captionDelays map (for cross-layer rendering)
    return designData.captionDelays[videoElementId];
  } else {
    // Fall back to caption's own delay or 0
    return captionData.delay ?? 0;
  }
};

/**
 * Pauses and seeks CSS animations on caption segment elements.
 * This allows Puppeteer to capture screenshots at specific animation frames.
 *
 * @param captionElementId - The ID of the caption element
 * @param adjustedTime - The adjusted playback time in milliseconds (media time, not timeline time)
 */
const pauseAndSeekCaptionAnimations = (
  captionElementId: string,
  adjustedTime: number
): void => {
  // Get design data from window (set by RenderContainer)
  const designData = window.renderDesignData;
  if (!designData) return;

  // Get caption element data
  const captionData = designData.elements[captionElementId] as
    | CaptionElementData
    | undefined;
  if (!captionData || captionData.type !== "caption") return;

  // Find the caption element in DOM
  const captionElement = document.getElementById(captionElementId);
  if (!captionElement) return;

  // Find all caption segment elements
  const segmentElements = captionElement.querySelectorAll(
    '[class*="captionSegment"]'
  );

  // If we're before the caption should start (delay not elapsed),
  // hide all segments entirely. Caption component may show a "preview"
  // at time 0, but for render routes we need to respect the delay.
  if (adjustedTime < 0) {
    segmentElements.forEach(segmentElement => {
      const htmlElement = segmentElement as HTMLElement;
      htmlElement.style.animation = "none";
      htmlElement.style.opacity = "0";
      htmlElement.style.visibility = "hidden";
    });
    return;
  }

  // Get animation type from displaySettings
  const animationType =
    captionData.displaySettings?.captionAnimation || "appear";
  const animationConfig = CAPTION_ANIMATION_CONFIG[animationType];

  // No animation for "appear" type - segments are already visible and positioned
  if (!animationConfig || animationConfig.duration === 0) return;

  // Filter to only segments with the animation class
  const animatedSegments = Array.from(segmentElements).filter(el =>
    el.className.includes(animationConfig.className)
  );

  animatedSegments.forEach((segmentElement, index) => {
    const htmlElement = segmentElement as HTMLElement;

    // Get segment timing data
    const segments = captionData.segments || [];
    const segment = segments[index];

    // Calculate segment start time in milliseconds (within media timeline)
    // Segments use seconds, so multiply by 1000
    const segmentStartTime =
      ((segment?.start ?? segment?.startTime) || 0) * 1000;

    // Calculate seek time relative to segment start
    // Use adjustedTime (time within media) not currentTime (timeline time)
    const timeSinceSegmentStart = adjustedTime - segmentStartTime;

    // Clamp seek time between 0 and animation duration
    const seekTime = Math.min(
      Math.max(0, timeSinceSegmentStart),
      animationConfig.duration
    );

    // At time 0, apply initial styles directly and disable animation
    // (same pattern as animationUtils.js where initial styles are set on elements)
    // CSS animations override inline styles, so we must disable the animation
    if (seekTime === 0 && animationConfig.initialStyles) {
      // Disable animation so inline styles take effect
      htmlElement.style.animation = "none";
      // Apply initial keyframe styles
      for (const [property, value] of Object.entries(
        animationConfig.initialStyles
      )) {
        htmlElement.style.setProperty(property, value);
      }
    } else {
      // For times > 0, clear any initial styles and use negative delay to seek
      // Clear animation: none if it was set at time 0
      htmlElement.style.removeProperty("animation");
      // Clear visibility/opacity that may have been set when adjustedTime < 0
      htmlElement.style.removeProperty("visibility");
      htmlElement.style.removeProperty("opacity");
      // Clear initial styles that may have been set at time 0
      if (animationConfig.initialStyles) {
        for (const property of Object.keys(animationConfig.initialStyles)) {
          htmlElement.style.removeProperty(property);
        }
      }
      // Use negative delay to seek into the animation
      htmlElement.style.animationPlayState = "paused";
      htmlElement.style.animationDelay = `-${seekTime}ms`;
      htmlElement.style.animationFillMode = "both";
    }
  });
};

/**
 * Updates multiple captions at the given playback time.
 * Sets the global playbackTime once, then processes all captions.
 *
 * @param elementIds - Array of caption element IDs to update
 * @param currentTime - The current playback time in milliseconds (timeline time)
 * @returns Promise that resolves when all captions have been updated and painted
 */
export const renderCaptions = (
  elementIds: string[],
  currentTime: number = 0
): Promise<void> => {
  // Ensure window.easil exists
  if (!window.easil) {
    window.easil = {};
  }

  // Set playbackTime to TIMELINE TIME once for all captions
  window.easil.playbackTime = currentTime;

  // Wait for two animation frames:
  // 1. First frame: Caption's polling picks up the playbackTime
  // 2. Second frame: DOM updates are painted
  return new Promise(resolve => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        // Pause and seek CSS animations for each caption
        elementIds.forEach(elementId => {
          const effectiveDelay = getEffectiveDelay(elementId);
          const mediaTime = currentTime - effectiveDelay;
          pauseAndSeekCaptionAnimations(elementId, mediaTime);
        });
        resolve();
      });
    });
  });
};

/**
 * Updates a single caption at the given playback time.
 * Convenience wrapper around renderCaptions for single caption use.
 *
 * @param elementId - The unique ID of the caption element
 * @param currentTime - The current playback time in milliseconds (timeline time)
 * @returns Promise that resolves when the caption has been updated and painted
 */
export const renderCaption = (
  elementId: string,
  currentTime: number = 0
): Promise<void> => {
  return renderCaptions([elementId], currentTime);
};

// Type declaration for global window object
// Note: renderDesignData is already declared in renderCssAnimation.ts
declare global {
  interface Window {
    /** Updates a single caption at a given playback time. */
    renderCaption?: typeof renderCaption;
    /** Updates multiple captions at a given playback time (preferred). */
    renderCaptions?: typeof renderCaptions;
    /** Easil global namespace for caption and other APIs */
    easil?: {
      /** Global playback time in milliseconds - polled by Caption component */
      playbackTime?: number;
      /** Caption preview segment data */
      captionPreviewSegment?: {
        elementId: string;
        segmentIndex: number;
      };
    };
  }
}

/**
 * Registers the renderCaption and renderCaptions functions on the window object
 * for access by Puppeteer.
 */
export const registerRenderCaption = (): void => {
  window.renderCaption = renderCaption;
  window.renderCaptions = renderCaptions;
};

export default renderCaption;
